import { memoize, sortBy } from 'lodash'
import { createSelector } from 'reselect'
import { BudgetRequestsState } from 'src/app/BudgetsRequests/redux/budgetRequest.reducer'
import {
  BudgetRequestListModel,
  BudgetRequestListResponse,
} from 'src/models/budgetRequest.models'
import { PaginationResponse } from 'src/models/pagination.models'
import { RootState } from 'src/redux/createRootReducer'
import {
  selectBudgetsNeedsApprovalMatch,
  selectBudgetsRequestsMatch,
  selectMyWalletBudgetsPendingMatch,
} from 'src/redux/selectors/router.selectors'

export const selectBudgetRequestsState = (
  state: RootState,
): BudgetRequestsState => state.app.budgetRequest

export const selectFetchingBudgetRequests = (state: RootState): boolean =>
  state.app.budgetRequest.loading

export const selectBudgetRequestListResponsesFromState = (
  state: RootState,
): Record<string, BudgetRequestListResponse> =>
  state.app.budgetRequest.listResponses

export const selectBudgetRequestListResponsesCount = (
  state: RootState,
): number => Object.values(state.app.budgetRequest.listResponses).length

export const selectBudgetRequestListModelsFromState = createSelector(
  [selectBudgetRequestListResponsesFromState],
  (budgetRequestListResponse) =>
    Object.values(budgetRequestListResponse).reduce<
      Record<BudgetRequestListResponse['id'], BudgetRequestListModel>
    >(
      (acc, curr) => ({
        ...acc,
        [curr.id]: new BudgetRequestListModel(curr),
      }),
      {},
    ),
)

export const selectBudgetRequestsRequesterMe = (
  state: RootState,
): BudgetRequestListModel[] =>
  state.app.budgetRequest.requesterMe.itemIDs.map(
    (id) =>
      new BudgetRequestListModel(state.app.budgetRequest.listResponses[id]),
  )

export const selectBudgetRequestsApproverMe = (
  state: RootState,
): BudgetRequestListModel[] =>
  state.app.budgetRequest.approverMe.itemIDs.map(
    (id) =>
      new BudgetRequestListModel(state.app.budgetRequest.listResponses[id]),
  )

export const selectBudgetRequestApproverMeCount = (state: RootState): number =>
  state.app.budgetRequest.approverMe.pagination.totalItems

export const selectBudgetRequestRequesterMeCount = (state: RootState): number =>
  state.app.budgetRequest.requesterMe.pagination.totalItems

export const selectBudgetRequestsRequesterMePagination = (
  state: RootState,
): PaginationResponse => state.app.budgetRequest.requesterMe.pagination

export const selectBudgetRequestsApproverMePagination = (
  state: RootState,
): PaginationResponse => state.app.budgetRequest.approverMe.pagination

export const selectBudgetRequestPaginationByRole = memoize(
  (role: 'requester' | 'approver') =>
    (state: RootState): PaginationResponse =>
      state.app.budgetRequest[`${role}Me`].pagination,
)

export const selectBudgetRequestCountByRole = memoize(
  (role: 'requester' | 'approver') =>
    (state: RootState): number =>
      state.app.budgetRequest[`${role}Me`].pagination.totalItems,
)

export const selectBudgetRequestsByRole = memoize(
  (role: 'requester' | 'approver') =>
    (state: RootState): BudgetRequestListModel[] =>
      sortBy(
        state.app.budgetRequest[`${role}Me`].itemIDs.map(
          (id) =>
            new BudgetRequestListModel(
              state.app.budgetRequest.listResponses[id],
            ),
        ),
        'updatedAt',
      ).reverse(),
)

export const selectBudgetRequestRole = createSelector(
  [
    selectBudgetsRequestsMatch,
    selectBudgetsNeedsApprovalMatch,
    selectMyWalletBudgetsPendingMatch,
  ],
  (requests, needsApproval, requester) => {
    if (requests || needsApproval) {
      return 'approver'
    }
    if (requester) {
      return 'requester'
    }
    throw new Error('No role found')
  },
)
